<template>
  <div class="py-12 sm:py-16 md:py-20 lg:py-24 grid lg:grid-cols-2 gap-8">
    <div class="hidden lg:block relative overflow-hidden rounded-2xl aspect-video lg:aspect-auto">
      <img
        :src="firstTimeRentingImage"
        alt="Family camping in an RV"
        class="absolute inset-0 w-full h-full object-cover"
      >
    </div>

    <div>
      <THeading
        :level="3"
        tag="h2"
        class="mb-2"
      >
        {{ t('title') }}
      </THeading>

      <TBody
        size="lg"
        variant="muted"
        class="mb-8"
      >
        {{ t('content') }}
      </TBody>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <NuxtLink
          v-for="item in items"
          :key="item.title"
          :to="item.to"
          class="bg-white border border-gray-300 rounded-2xl p-4 text-left hover:bg-gray-50 hover:border-gray-500 transition-colors duration-300"
        >
          <span class="flex gap-2 mb-6 text-lg">
            <TIcon
              :icon="item.icon"
              size="xl"
              fixed-width
              class="mt-0.5"
            />
            <span>{{ item.title }}</span>
          </span>

          <TBody variant="muted">
            {{ item.content }}
          </TBody>
        </NuxtLink>
      </div>
    </div>

    <div class="hidden lg:block" />

    <div>
      <NuxtLink
        :to="localePath({ name: 'blog' })"
        class="inline-block bg-white text-primary-950 border border-primary-950 hover:bg-gray-100 focus-visible:outline-primary-950 py-3 px-6 rounded-3xl text-base font-semibold no-underline"
      >
        {{ t('explore') }}
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ROUTE_BASE_NAME } from '~/constants'
import type { IconName } from '@fortawesome/fontawesome-svg-core'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const { isInCa, isInUs } = useGeolocation()

const { getStaticImage, presets } = useImage()
const firstTimeRentingImage = computed(() => getStaticImage({
  path: '/web-app/first-time-renting.webp',
  preset: presets.LANDSCAPE_MD,
}))

const computedTopDestinationsTo = computed(() => {
  if (isInCa.value) {
    return localePath({ name: ROUTE_BASE_NAME.rv_rentals_destination, params: { destinationPath: [formatCountryCodeAsSlug('CA')] } })
  }

  if (isInUs.value) {
    return localePath({ name: ROUTE_BASE_NAME.rv_rentals_destination, params: { destinationPath: [formatCountryCodeAsSlug('US')] } })
  }

  return localePath({ name: 'rv-rentals' })
})

const rvrRental101BlogSlug = computed(() => {
  if (locale.value === 'fr') {
    return 'comment-louer-un-vr-le-guide-du-debutant'
  }
  return 'how-to-rent-an-rv-a-complete-beginner-friendly-guide'
})

const items = computed<{ title: string, content: string, icon: IconName, to: string }[]>(() => [
  { title: t('rvRental101.title'), content: t('rvRental101.content'), icon: 'book-open', to: localePath({ name: 'blog-slug', params: { slug: rvrRental101BlogSlug.value } }) },
  { title: t('topDestinations.title'), content: t('topDestinations.content'), icon: 'location-dot', to: computedTopDestinationsTo.value },
  { title: t('safetyInsurance.title'), content: t('safetyInsurance.content'), icon: 'shield', to: localePath({ name: 'insurance-and-protection' }) },
  { title: t('travelTips.title'), content: t('travelTips.content'), icon: 'lightbulb', to: localePath({ name: 'blog' }) },
])
</script>

<i18n lang="json">
  {
    "en": {
      "title": "First time renting?",
      "content": "Check out our comprehensive blog library for helpful guides, tips, and inspiration for your RV adventure.",

      "rvRental101": {
        "title": "RV Rental 101",
        "content": "Everything you need to choose the right RV, understanding insurance, compare costs, and prepare for your first rental."
      },

      "topDestinations": {
        "title": "Top RV Destinations",
        "content": "Browse our most popular cities, campgrounds, and scenic routes across Canada and the U.S. — perfect for planning your next road trip."
      },

      "safetyInsurance": {
        "title": "Safety & Insurance",
        "content": "Get clear, easy explanations of coverage, roadside assistance, and safe-driving tips for motorhomes and travel trailers."
      },

      "travelTips": {
        "title": "Travel Tips",
        "content": "Packing checklists, campground tips, road trip planning, seasonal travel guides, and everything else to make your trip smoother."
      },

      "explore": "Explore RV travel guides"
    },
    "fr": {
      "title": "C’est votre première location?",
      "content": "Consultez notre bibliothèque de blogs complète pour trouver des guides utiles, des conseils et de l’inspiration pour votre aventure en VR.",

      "rvRental101": {
        "title": "Location de VR 101",
        "content": "Tout ce dont vous avez besoin pour choisir le bon VR, comprendre les assurances, comparer les coûts et préparer votre première location."
      },

      "topDestinations": {
        "title": "Les meilleures destinations en VR",
        "content": "Parcourez nos villes, terrains de camping et routes panoramiques les plus populaires au Canada et aux États-Unis, parfaits pour planifier votre prochain road trip."
      },

      "safetyInsurance": {
        "title": "Sécurité et assurance",
        "content": "Obtenez des explications claires et simples sur la couverture, l’assistance routière et des conseils de conduite sécuritaire pour les motorisés et les roulottes."
      },

      "travelTips": {
        "title": "Conseils de voyage",
        "content": "Listes de contrôle pour faire vos bagages, conseils pour les terrains de camping, planification de road trips, guides de voyage saisonniers et tout ce dont vous avez besoin pour que votre voyage se déroule sans encombre."
      },

      "explore": "Découvrez les guides de voyage en VR"
    }
  }
</i18n>
